import { createFeature, createReducer, on } from '@ngrx/store';

import {
  initialPersonalInformation,
  initialStatus,
  initialTerminationInformation,
  LoadingStatus,
  PersonalInformation,
  TerminationInformation,
} from '@features/termination/interfaces';
import { Customer, Product } from '@features/termination/models';

import * as fromActions from '../../actions';

export const terminationFeatureName = 'terminationInformation';

export interface TerminationState {
  customer: Customer | undefined;
  customerInformationLoadingStatus: LoadingStatus;
  customerTelematicsId: string;
  terminationInformation: TerminationInformation;

  groundsList: string[];
  bankingProducts: Product[];
  insurances: Product[];
  personalInformation: PersonalInformation;

  productListLoadingStatus: LoadingStatus;
  emailLoadingStatus: LoadingStatus;
  requestLoadingStatus: LoadingStatus;
  stepInProgress: number;
}

export const initialState: TerminationState = {
  customer: undefined,
  customerInformationLoadingStatus: initialStatus,
  customerTelematicsId: '',
  terminationInformation: initialTerminationInformation,
  stepInProgress: 0,

  groundsList: [],
  bankingProducts: [],
  insurances: [],

  personalInformation: initialPersonalInformation,

  emailLoadingStatus: initialStatus,
  requestLoadingStatus: initialStatus,
  productListLoadingStatus: initialStatus,
};

export const TerminationReducers = createReducer(
  initialState,
  on(fromActions.CustomerInformationLoad, (state: TerminationState): TerminationState => ({
    ...state,
    customerInformationLoadingStatus: {
      ...initialStatus,
      isLoading: true,
    },
  })),
  on(
    fromActions.CustomerInformationLoadComplete,
    (state: TerminationState, { customer, subscription }): TerminationState => ({
      ...state,
      customerInformationLoadingStatus: {
        ...initialStatus,
        isLoaded: true,
      },
      customer: customer,
      customerTelematicsId: subscription?.customerTelematicsId,
    }),
  ),
  on(
    fromActions.CustomerInformationLoadError,
    (state: TerminationState, { error }): TerminationState => ({
      ...state,
      customerInformationLoadingStatus: {
        ...initialStatus,
        error,
      },
    }),
  ),
  on(fromActions.ProductListLoad, (state: TerminationState): TerminationState => ({
    ...state,
    productListLoadingStatus: {
      ...initialStatus,
      isLoading: true,
    },
  })),
  on(
    fromActions.ProductListLoadComplete,
    (state: TerminationState, { groundsList, bank, insurances }): TerminationState => ({
      ...state,
      productListLoadingStatus: {
        ...initialStatus,
        isLoaded: true,
      },
      groundsList: groundsList,
      bankingProducts: bank,
      insurances: insurances,
    }),
  ),
  on(fromActions.ProductListLoadError, (state: TerminationState, { error }): TerminationState => ({
    ...state,
    productListLoadingStatus: {
      ...initialStatus,
      error,
    },
  })),
  on(
    fromActions.TerminationInformationSave,
    (state: TerminationState, { terminationInformation }): TerminationState => ({
      ...state,
      terminationInformation: terminationInformation,
    }),
  ),
  on(fromActions.UpdateStep, (state: TerminationState, { step }): TerminationState => ({
    ...state,
    stepInProgress: step,
  })),
  on(
    fromActions.UpdatePersonalInformation,
    (state: TerminationState, { personalInformation }): TerminationState => ({
      ...state,
      personalInformation,
    }),
  ),
  on(fromActions.SendEmailLoad, (state: TerminationState): TerminationState => ({
    ...state,
    emailLoadingStatus: {
      ...initialStatus,
      isLoading: true,
    },
  })),
  on(fromActions.SendEmailLoadComplete, (state: TerminationState): TerminationState => ({
    ...state,
    emailLoadingStatus: {
      ...initialStatus,
      isLoaded: true,
    },
  })),
  on(fromActions.SendEmailLoadError, (state: TerminationState, { error }): TerminationState => ({
    ...state,
    emailLoadingStatus: {
      ...initialStatus,
      error,
    },
  })),
  on(fromActions.sendRequestActions.send, (state: TerminationState): TerminationState => ({
    ...state,
    requestLoadingStatus: {
      ...initialStatus,
      isLoading: true,
    },
  })),
  on(fromActions.sendRequestActions.sendCompleted, (state: TerminationState): TerminationState => ({
    ...state,
    requestLoadingStatus: {
      ...initialStatus,
      isLoaded: true,
    },
  })),
  on(
    fromActions.sendRequestActions.sendFailed,
    (state: TerminationState, { error }): TerminationState => ({
      ...state,
      requestLoadingStatus: {
        ...initialStatus,
        error,
      },
    }),
  ),
);

export const terminationFeature = createFeature({
  name: terminationFeatureName,
  reducer: TerminationReducers,
});
